import * as angular from 'angular';
import _ from 'lodash';
import { ModelAccessorInjectables, ModelAccessorBase } from '../model_accessor';
import { ModeModelBase } from '../model';
import { Injectable } from '@angular/core';
import { DEFAULT_CHART_FORMAT } from '@mode/shared/util-js';

const DEFAULT_CHART_TITLE = 'Chart';

export class ChartModel extends ModeModelBase {
  view: any;
  vizLink: string;
  typeIcons: any;
  view_version: number;
  view_vegas: any;
  token: string;

  override _name = 'chart';
  override idAttribute = 'token';
  override belongsTo = 'query';
  override parentIdAttribute = 'token';
  override attributes = [
    { name: 'view', default: null, type: 'text' },
    { name: 'view_vegas', default: null, type: 'text' },
    { name: 'view_version', default: 1, type: 'number' },
    { name: 'created_at', default: '' },
    { name: 'color_palette_token', default: '' },
  ];
  override hasOne = [
    {
      name: 'colorPalette',
      as: 'colorPalette',
      idAttribute: 'token',
      default: '',
    },
  ];

  override initialize(resource) {
    if (this.view && !this.view.linePlusBar) {
      this.view.linePlusBar = {};
    }

    if (resource && resource.hasLink('report_viz_web')) {
      this.vizLink = resource.links('report_viz_web').href;
    }

    this.typeIcons = {
      bar: 'icon-chart-grouped-bar-gray',
      stackedBar: 'icon-chart-bar-gray',
      bigNumber: 'icon-chart-bignumber-gray',
      pivotTable: 'icon-chart-pivot-gray',
      linePlusBar: 'icon-chart-linebar-gray',
      line: 'icon-chart-line-gray',
      area: 'icon-chart-area-gray',
      totalArea: 'icon-chart-area100-gray',
      scatter: 'icon-chart-scatter-gray',
      pie: 'icon-chart-pie-gray',
      donut: 'icon-chart-donut-gray',
    };
  }

  getTitle() {
    return this.view_version === 1 ? this.view.chartTitle : this.view_vegas.title;
  }

  getTitleOrDefault() {
    let type = this.view_version === 1 ? this.view.selectedChart : this.view_vegas.chartType;
    let suffix = DEFAULT_CHART_TITLE;

    if ((this.view.bar && this.view.bar.stacked) || type === 'stackedBar') {
      type = 'Bar';
    } else if (type === 'bar') {
      type = 'Grouped Bar';
    } else if (type === 'pie' && this.view.pie && this.view.pie.donut) {
      type = 'Donut';
    } else if (type === 'bigNumber') {
      type = 'Big Number';
    } else if (type === 'pivotTable') {
      type = 'Pivot Table';
      suffix = '';
    } else if (type === 'linePlusBar') {
      type = 'Line + Bar';
    } else if (type === 'totalArea') {
      type = 'Area';
    }

    return this.getTitle() || (type ? type[0].toUpperCase() + type.slice(1) + ' ' + suffix : suffix);
  }

  getVizReportRunLink(reportRunToken) {
    return this._resource.hasLink('report_run_viz_web')
      ? this._resource.links('report_run_viz_web').$expand({ token: reportRunToken }, { excludeSSLPort: true })
      : '';
  }

  renderEmbed(reportRunToken) {
    return angular.extend({}, this.view_version === 2 ? this.view_vegas : this.view, {
      _dataset: this.parent,
      _token: this.token,
      _vizLink: this.vizLink,
      _vizReportRunLink: this.getVizReportRunLink(reportRunToken),
    });
  }
}

const buildColorPalette = (chart) => {
  return chart
    .colorPalette()
    .then((colorPalette) => {
      if (chart.view_version === 1 && chart.view.selectedChart === 'bigNumber') {
        chart.view.bigNumber.colors = colorPalette.value.split(',');
        chart.view.bigNumber.colorPalette = colorPalette.token;
        return chart;
      }
      if (!chart.view_vegas.format) {
        chart.view_vegas.format = angular.copy(DEFAULT_CHART_FORMAT);
      }
      chart.view_vegas.format.colors.custom = colorPalette.value.split(',');
      return chart;
    })
    .catch((e) => {
      console.error('Color palette not embedded in chart model');
      if (chart.view_vegas && chart.view_vegas.format && chart.view_vegas.format.colors) {
        // hard coded mode 10 values in case no palettes are found
        chart.view_vegas.format.colors.custom = [
          '#37B067',
          '#6296BC',
          '#EDB40D',
          '#7FD7C1',
          '#9F8CAE',
          '#EB6672',
          '#376C72',
          '#EE9DCC',
          '#E3791A',
          '#9F765E',
        ];
      }
      return chart;
    });
};

@Injectable({ providedIn: 'root' })
export class ChartModelAccessor extends ModelAccessorBase<ChartModel> {
  override name = 'chart';
  override idAttribute = 'token';
  override parentModel = 'query';
  override parentIdAttribute = 'token';
  override createAttributes = ['query'];

  constructor(injectables: ModelAccessorInjectables) {
    super(injectables, 'migrated', 'mode.chart');
  }

  buildChartWithColorPalette = buildColorPalette;

  buildChartsWithColorPalettes(charts) {
    return this.injectables.$q.all(_.map(charts, buildColorPalette));
  }

  override createModel(resource, opts?) {
    const model = new ChartModel(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }
}
